import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  useMediaQuery,
  useTheme,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import ManagerReportsSection from '../../components/manager/ManagerReportsSection';
import VehicleScheduleSection from '../../components/manager/VehicleScheduleSection';
import api from '../../services/api';

const ManagerVehicles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [vehicleDetailDialogOpen, setVehicleDetailDialogOpen] = useState(false);
  const [vehicleDetailTabValue, setVehicleDetailTabValue] = useState(0);
  const [detailVehicle, setDetailVehicle] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editFormData, setEditFormData] = useState({
    license_plate: '',
    make: '',
    model: '',
    year: '',
    status: 'idle',
    diesel_quantity: '',
    def_quantity: '',
  });
  const [formData, setFormData] = useState({
    license_plate: '',
    make: '',
    model: '',
    year: '',
    status: 'idle',
    diesel_quantity: '158.5',
    def_quantity: '',
  });
  const [assignData, setAssignData] = useState({
    driver_ids: [],
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    fetchVehicles();
    fetchDrivers();
  }, []);

  const fetchVehicles = async () => {
    try {
      setApiError('');
      const response = await api.get('/vehicles/');
      console.log('Vehicles API response:', response);
      console.log('Vehicles response.data:', response.data);
      
      // Handle paginated response
      let vehiclesData = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          vehiclesData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          // Paginated response
          vehiclesData = response.data.results;
        } else if (typeof response.data === 'object') {
          // Try to extract data from object
          vehiclesData = response.data.data || response.data.vehicles || [];
        }
      }
      
      console.log('Processed vehicles data:', vehiclesData);
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      console.error('Error response:', error.response);
      setVehicles([]);
      const errorMsg = error.response?.data?.detail || 
                      error.response?.data?.error || 
                      error.message ||
                      'Failed to load vehicles';
      setApiError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/managers/drivers/');
      // Ensure response.data is an array
      const driversData = Array.isArray(response.data) ? response.data : [];
      setDrivers(driversData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setDrivers([]);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setError('');
    setSuccess('');
    setFormData({
      license_plate: '',
      make: '',
      model: '',
      year: '',
      status: 'idle',
      diesel_quantity: '',
      def_quantity: '',
    });
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setError('');
    setSuccess('');
  };

  const handleOpenAssignDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setAssignDialogOpen(true);
    setError('');
    setSuccess('');
    setAssignData({
      driver_ids: Array.isArray(vehicle?.assigned_driver_ids) ? vehicle.assigned_driver_ids : [],
    });
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setSelectedVehicle(null);
    setError('');
    setSuccess('');
  };

  const handleOpenEditDialog = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditDialogOpen(true);
    setError('');
    setSuccess('');
    setEditFormData({
      license_plate: vehicle?.license_plate || '',
      make: vehicle?.make || '',
      model: vehicle?.model || '',
      year: vehicle?.year || '',
      status: vehicle?.status || 'idle',
      diesel_quantity: vehicle?.diesel_quantity || '',
      def_quantity: vehicle?.def_quantity || '',
    });
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedVehicle(null);
    setError('');
    setSuccess('');
  };

  const getVehicleDisplayName = (vehicle) => {
    if (!vehicle) return 'N/A';
    const label = `${vehicle.make || ''} ${vehicle.model || ''}`.trim();
    return vehicle.license_plate
      ? `${label || 'Vehicle'} (${vehicle.license_plate})`
      : label || 'Vehicle';
  };

  const handleOpenVehicleDetailDialog = (vehicle) => {
    setDetailVehicle(vehicle);
    setVehicleDetailTabValue(0);
    setVehicleDetailDialogOpen(true);
  };

  const handleCloseVehicleDetailDialog = () => {
    setVehicleDetailDialogOpen(false);
    setVehicleDetailTabValue(0);
    setDetailVehicle(null);
  };

  const handleEditChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateVehicle = async () => {
    setError('');
    setSuccess('');

    if (!selectedVehicle?.id) {
      setError('Invalid vehicle selected');
      return;
    }

    if (!editFormData.license_plate || !editFormData.make || !editFormData.model || !editFormData.year) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const payload = {
        license_plate: editFormData.license_plate.trim(),
        make: editFormData.make.trim(),
        model: editFormData.model.trim(),
        year: parseInt(editFormData.year),
        status: editFormData.status,
        diesel_quantity: editFormData.diesel_quantity ? parseFloat(editFormData.diesel_quantity) : 0,
        def_quantity: editFormData.def_quantity ? parseFloat(editFormData.def_quantity) : 0,
      };

      await api.patch(`/vehicles/${selectedVehicle.id}/`, payload);
      setSuccess('Vehicle updated successfully');
      fetchVehicles();
      setTimeout(() => {
        handleCloseEditDialog();
      }, 1500);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      const data = error.response?.data || {};
      let errorMessage = 'Failed to update vehicle';
      
      if (data.license_plate) {
        errorMessage = Array.isArray(data.license_plate) 
          ? `License plate: ${data.license_plate[0]}` 
          : `License plate: ${data.license_plate}`;
      } else if (data.make) {
        errorMessage = Array.isArray(data.make) 
          ? `Make: ${data.make[0]}` 
          : `Make: ${data.make}`;
      } else if (data.model) {
        errorMessage = Array.isArray(data.model) 
          ? `Model: ${data.model[0]}` 
          : `Model: ${data.model}`;
      } else if (data.year) {
        errorMessage = Array.isArray(data.year) 
          ? `Year: ${data.year[0]}` 
          : `Year: ${data.year}`;
      } else if (data.status) {
        errorMessage = Array.isArray(data.status) 
          ? `Status: ${data.status[0]}` 
          : `Status: ${data.status}`;
      } else if (data.detail) {
        errorMessage = data.detail;
      } else if (data.error) {
        errorMessage = data.error;
      }
      
      setError(errorMessage);
    }
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAssignChange = (e) => {
    const value = e.target.value;
    setAssignData({
      driver_ids: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleCreate = async () => {
    setError('');
    setSuccess('');

    if (!formData.license_plate || !formData.make || !formData.model || !formData.year) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      // Validate year is a number
      const year = parseInt(formData.year);
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear() + 1) {
        setError('Please enter a valid year');
        return;
      }

      const payload = {
        license_plate: formData.license_plate.trim(),
        make: formData.make.trim(),
        model: formData.model.trim(),
        year: year,
        status: formData.status || 'idle',
        diesel_quantity: formData.diesel_quantity ? parseFloat(formData.diesel_quantity) : 158.5,
        def_quantity: formData.def_quantity ? parseFloat(formData.def_quantity) : 0,
      };
      
      await api.post('/vehicles/', payload);
      setSuccess('Vehicle created successfully');
      fetchVehicles();
      setTimeout(() => {
        handleCloseCreateDialog();
      }, 1500);
    } catch (error) {
      console.error('Error creating vehicle:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      let errorMessage = 'Failed to create vehicle';
      
      if (error.response?.data) {
        const data = error.response.data;
        console.log('Error data:', JSON.stringify(data, null, 2));
        
        // Handle validation errors
        if (data.license_plate) {
          errorMessage = Array.isArray(data.license_plate) 
            ? `License plate: ${data.license_plate[0]}` 
            : `License plate: ${data.license_plate}`;
        } else if (data.make) {
          errorMessage = Array.isArray(data.make) 
            ? `Make: ${data.make[0]}` 
            : `Make: ${data.make}`;
        } else if (data.model) {
          errorMessage = Array.isArray(data.model) 
            ? `Model: ${data.model[0]}` 
            : `Model: ${data.model}`;
        } else if (data.year) {
          errorMessage = Array.isArray(data.year) 
            ? `Year: ${data.year[0]}` 
            : `Year: ${data.year}`;
        } else if (data.status) {
          errorMessage = Array.isArray(data.status) 
            ? `Status: ${data.status[0]}` 
            : `Status: ${data.status}`;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) 
            ? data.non_field_errors[0] 
            : data.non_field_errors;
        } else if (typeof data === 'object') {
          // Try to extract first error message
          const firstKey = Object.keys(data)[0];
          if (firstKey) {
            const firstError = data[firstKey];
            errorMessage = Array.isArray(firstError) 
              ? `${firstKey}: ${firstError[0]}` 
              : `${firstKey}: ${firstError}`;
          } else {
            // If no keys, show the whole object as string
            errorMessage = JSON.stringify(data);
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    }
  };

  const handleAssign = async () => {
    setError('');
    setSuccess('');

    if (!selectedVehicle?.id) {
      setError('Invalid vehicle selected');
      return;
    }

    // Allow unassigning (empty driver_id is valid)
    try {
      const payload = {
        vehicle_id: selectedVehicle.id,
        driver_ids: assignData.driver_ids || [],
      };
      
      await api.post('/managers/assign-vehicle/', payload);
      setSuccess('Vehicle drivers updated successfully');
      fetchVehicles();
      fetchDrivers();
      setTimeout(() => {
        handleCloseAssignDialog();
      }, 1500);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to assign vehicle');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      idle: 'success',
      active: 'primary',
      refueling: 'warning',
      out_of_service: 'error',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Vehicles</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
            Create Vehicle
          </Button>
        </Box>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>License Plate</TableCell>
                <TableCell>Make & Model</TableCell>
                <TableCell>Year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Diesel (gal)</TableCell>
                <TableCell>DEF (gal)</TableCell>
                <TableCell>Assigned Drivers</TableCell>
                <TableCell>Current Job</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!Array.isArray(vehicles) || vehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No vehicles found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                vehicles.map((vehicle) => (
                  <TableRow key={vehicle?.id || Math.random()}>
                    <TableCell>{vehicle?.license_plate || 'N/A'}</TableCell>
                    <TableCell>
                      {vehicle?.make || ''} {vehicle?.model || ''}
                    </TableCell>
                    <TableCell>{vehicle?.year || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={vehicle?.status === 'out_of_service' ? 'Out of Service' : (vehicle?.status || 'unknown')}
                        color={getStatusColor(vehicle?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{vehicle?.diesel_quantity != null ? `${parseFloat(vehicle.diesel_quantity).toFixed(2)} gal` : '0.00 gal'}</TableCell>
                    <TableCell>{vehicle?.def_quantity != null ? `${parseFloat(vehicle.def_quantity).toFixed(2)} gal` : '0.00 gal'}</TableCell>
                    <TableCell>{vehicle?.driver_name || 'Not assigned'}</TableCell>
                    <TableCell>
                      {vehicle?.job_info ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Job: {vehicle.job_info.id ? vehicle.job_info.id.substring(0, 8) : 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                            {vehicle.job_info.location || 'N/A'}
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={vehicle.job_info.status || 'unknown'}
                              size="small"
                              color={
                                vehicle.job_info.status === 'complete' ? 'success' :
                                vehicle.job_info.status === 'active' ? 'primary' :
                                vehicle.job_info.status === 'accepted' ? 'info' :
                                vehicle.job_info.status === 'pending' ? 'warning' : 'default'
                              }
                              sx={{ height: '20px', fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', mt: 0.5 }}>
                            {(() => {
                              const dieselQty = parseFloat(vehicle.job_info.diesel_quantity) || 0;
                              const defQty = parseFloat(vehicle.job_info.def_quantity) || 0;
                              if (dieselQty > 0 || defQty > 0) {
                                return [
                                  dieselQty > 0 && `${dieselQty} gal Diesel`,
                                  dieselQty > 0 && defQty > 0 && ' / ',
                                  defQty > 0 && `${defQty} gal DEF`
                                ].filter(Boolean).join('');
                              }
                              return '0 gal';
                            })()}
                          </Typography>
                        </Box>
                      ) : vehicle?.current_job ? (
                        <Typography variant="body2" color="text.secondary">
                          Job ID: {vehicle.current_job.substring(0, 8)}
                        </Typography>
                      ) : (
                        'None'
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenVehicleDetailDialog(vehicle)}
                        sx={{ mr: 1 }}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(vehicle)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => handleOpenAssignDialog(vehicle)}
                      >
                        Assign Drivers
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Vehicle Dialog */}
        <Dialog open={createDialogOpen} onClose={handleCloseCreateDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Create Vehicle</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="license_plate"
                label="License Plate"
                value={formData.license_plate}
                onChange={handleFormChange}
                required
                fullWidth
              />
              <TextField
                name="make"
                label="Make"
                value={formData.make}
                onChange={handleFormChange}
                required
                fullWidth
              />
              <TextField
                name="model"
                label="Model"
                value={formData.model}
                onChange={handleFormChange}
                required
                fullWidth
              />
              <TextField
                name="year"
                label="Year"
                type="number"
                value={formData.year}
                onChange={handleFormChange}
                required
                fullWidth
                inputProps={{ min: '1900', max: new Date().getFullYear() + 1 }}
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                  label="Status"
                >
                  <MenuItem value="idle">Idle</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="refueling">Refueling</MenuItem>
                  <MenuItem value="out_of_service">Out of Service</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Fuel Quantities
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="diesel_quantity"
                  label="Diesel quantity (gallons)"
                  type="number"
                  value={formData.diesel_quantity}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ 
                    step: '0.01',
                    min: 0
                  }}
                  helperText="Diesel remaining in gallons"
                />
                <TextField
                  name="def_quantity"
                  label="DEF quantity (gallons)"
                  type="number"
                  value={formData.def_quantity}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ 
                    step: '0.01',
                    min: 0
                  }}
                  helperText="DEF remaining in gallons"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained">
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assign Vehicle Dialog */}
        <Dialog 
          open={assignDialogOpen} 
          onClose={handleCloseAssignDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Assign Drivers to Vehicle</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {selectedVehicle && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Vehicle: {selectedVehicle?.license_plate || 'N/A'} - {selectedVehicle?.make || ''} {selectedVehicle?.model || ''}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Drivers</InputLabel>
                <Select
                  multiple
                  name="driver_ids"
                  value={assignData.driver_ids}
                  onChange={handleAssignChange}
                  label="Drivers"
                  renderValue={(selected) => {
                    if (!selected.length) return 'No drivers assigned';
                    return selected
                      .map((id) => drivers.find((d) => d.id === id)?.name || id.substring(0, 8))
                      .join(', ');
                  }}
                >
                  {!Array.isArray(drivers) || drivers.length === 0 ? (
                    <MenuItem disabled>No drivers available</MenuItem>
                  ) : (
                    drivers
                      .filter((driver) => driver?.status !== 'not_working' && driver?.status !== 'license_suspended')
                      .map((driver) => (
                        <MenuItem key={driver?.id || Math.random()} value={driver?.id}>
                          {driver?.name || 'Unknown'} ({driver?.status || 'unknown'})
                        </MenuItem>
                      ))
                  )}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                Select all drivers who may use this vehicle. Clear all selections to remove every assignment.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAssignDialog}>Cancel</Button>
            <Button onClick={handleAssign} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog 
          open={editDialogOpen} 
          onClose={handleCloseEditDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Edit Vehicle</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="license_plate"
                label="License Plate"
                value={editFormData.license_plate}
                onChange={handleEditChange}
                required
                fullWidth
              />

              <TextField
                name="make"
                label="Make"
                value={editFormData.make}
                onChange={handleEditChange}
                required
                fullWidth
              />

              <TextField
                name="model"
                label="Model"
                value={editFormData.model}
                onChange={handleEditChange}
                required
                fullWidth
              />

              <TextField
                name="year"
                label="Year"
                type="number"
                value={editFormData.year}
                onChange={handleEditChange}
                required
                fullWidth
                inputProps={{ min: 1900, max: 2100 }}
              />

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={editFormData.status}
                  onChange={handleEditChange}
                  label="Status"
                >
                  <MenuItem value="idle">Idle</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="refueling">Refueling</MenuItem>
                  <MenuItem value="out_of_service">Out of Service</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                Fuel Quantities
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="diesel_quantity"
                  label="Diesel quantity (gallons)"
                  type="number"
                  value={editFormData.diesel_quantity}
                  onChange={handleEditChange}
                  fullWidth
                  inputProps={{ 
                    step: '0.01',
                    min: 0
                  }}
                  helperText="Diesel remaining in gallons"
                />
                <TextField
                  name="def_quantity"
                  label="DEF quantity (gallons)"
                  type="number"
                  value={editFormData.def_quantity}
                  onChange={handleEditChange}
                  fullWidth
                  inputProps={{ 
                    step: '0.01',
                    min: 0
                  }}
                  helperText="DEF remaining in gallons"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseEditDialog}>Cancel</Button>
            <Button onClick={handleUpdateVehicle} variant="contained">
              Update Vehicle
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={vehicleDetailDialogOpen}
          onClose={handleCloseVehicleDetailDialog}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: { height: { xs: '100vh', sm: '90vh' } },
          }}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Vehicle Details - {getVehicleDisplayName(detailVehicle)}
              </Typography>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  handleCloseVehicleDetailDialog();
                  handleOpenEditDialog(detailVehicle);
                }}
              >
                Edit
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {detailVehicle && (
              <Box>
                <Tabs
                  value={vehicleDetailTabValue}
                  onChange={(e, newValue) => setVehicleDetailTabValue(newValue)}
                  sx={{ mb: 2 }}
                >
                  <Tab label="Profile" />
                  <Tab label="Schedule" />
                  <Tab label="Reports & Trends" />
                </Tabs>

                {vehicleDetailTabValue === 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Typography variant="h5">{getVehicleDisplayName(detailVehicle)}</Typography>
                          <Chip
                            label={
                              detailVehicle.status === 'out_of_service'
                                ? 'Out of Service'
                                : detailVehicle.status || 'unknown'
                            }
                            color={getStatusColor(detailVehicle.status)}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
                        >
                          Vehicle Information
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">License Plate</Typography>
                        <Typography variant="body1">{detailVehicle.license_plate || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Year</Typography>
                        <Typography variant="body1">{detailVehicle.year || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Make</Typography>
                        <Typography variant="body1">{detailVehicle.make || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Model</Typography>
                        <Typography variant="body1">{detailVehicle.model || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Status</Typography>
                        <Box>
                          <Chip
                            label={
                              detailVehicle.status === 'out_of_service'
                                ? 'Out of Service'
                                : detailVehicle.status || 'unknown'
                            }
                            color={getStatusColor(detailVehicle.status)}
                            size="small"
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}
                        >
                          Fuel On Board
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Diesel</Typography>
                        <Typography variant="body1">
                          {detailVehicle.diesel_quantity != null
                            ? `${parseFloat(detailVehicle.diesel_quantity).toFixed(2)} gal`
                            : '0.00 gal'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">DEF</Typography>
                        <Typography variant="body1">
                          {detailVehicle.def_quantity != null
                            ? `${parseFloat(detailVehicle.def_quantity).toFixed(2)} gal`
                            : '0.00 gal'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}
                        >
                          Assignment
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Assigned Drivers</Typography>
                        <Typography variant="body1">{detailVehicle.driver_name || 'Not assigned'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Current Job</Typography>
                        <Typography variant="body1">
                          {detailVehicle.job_info?.location
                            ? detailVehicle.job_info.location
                            : detailVehicle.current_job
                              ? `Job ${detailVehicle.current_job.substring(0, 8)}`
                              : 'None'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{ borderBottom: 1, borderColor: 'divider', pb: 1, mt: 2 }}
                        >
                          Record
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Vehicle ID</Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {detailVehicle.id || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Created At</Typography>
                        <Typography variant="body1">
                          {detailVehicle.created_at
                            ? new Date(detailVehicle.created_at).toLocaleString()
                            : 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                        <Typography variant="body1">
                          {detailVehicle.updated_at
                            ? new Date(detailVehicle.updated_at).toLocaleString()
                            : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {vehicleDetailTabValue === 1 && detailVehicle?.id && (
                  <VehicleScheduleSection
                    vehicleId={detailVehicle.id}
                    vehicleName={getVehicleDisplayName(detailVehicle)}
                    embedded
                  />
                )}

                {vehicleDetailTabValue === 2 && detailVehicle?.id && (
                  <ManagerReportsSection
                    vehicleId={detailVehicle.id}
                    vehicleName={getVehicleDisplayName(detailVehicle)}
                    embedded
                  />
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVehicleDetailDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default ManagerVehicles;



